import { Application, Container, Graphics, Sprite, Text, Texture } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { gsap } from 'gsap'
import { store } from '@/store/store'
import { selectCrashGame } from '@/store/slices/crashSlice'
import type { CrashGamePhase } from '@/types/crash'
import { SoundManager } from '../core/SoundManager'

type CloudSprite = Sprite & { baseSpeed: number; flow: number }

const RUNWAY_TIME = 0.6
const TAKEOFF_SFX_EXTRA_TIME = 1
const PLANE_ENTRY_DURATION = 1
const COUNTDOWN_INTERVAL_MS = 1000
const COUNTDOWN_EXIT_DURATION = 0.5
const COUNTDOWN_EXIT_SCALE = 2
const TIMER_PULSE_SCALE_FROM = 0.6
const TIMER_PULSE_SCALE_TO = 1.4
const PLANE_SCALE_FACTOR = 0.085
const PLANE_CLIMB_FACTOR = 0.32
const BLAST_SCALE_FACTOR = 0.45
const SFX_VOLUME_TAKEOFF = 0.4
const SFX_VOLUME_FLY = 0.15
const SFX_VOLUME_TIMER = 0.2
const SFX_VOLUME_CRASH = 0.2

export class FlyArea extends Container {
	private app: Application
	private skyOverlay!: Graphics
	private plane!: Spine
	private blast!: Spine
	private countdownInterval?: ReturnType<typeof setInterval>
	private cloudsActive = false
	private serverTime = 0
	private lastUpdate = 0
	private isWaiting = false
	private bgLayer!: Container
	private bg!: Sprite
	private bgPatterns: Sprite[] = []
	private bgDrift = false
	private bgScale = 1
	private patternTileWidth = 0
	private multiplierText!: Text
	private timerText!: Text
	private flyWidth = 0
	private flyHeight = 0
	private takeoffPlayed = false
	private flyLoopStarted = false
	private startX = 0
	private startY = 0
	private cloudsContainer!: Container
	private clouds: CloudSprite[] = []
	private isFlying = false
	private isCrashed = false
	private areaMask!: Graphics
	private viewWidth = 0
	private viewHeight = 0
	private placeBetText!: Text
	private cloudTexture!: Texture
	private patternTexture!: Texture
	private skyAnimating = false
	private previousPhase: CrashGamePhase = 'idle'
	private unsubscribeStore: (() => void) | null = null

	constructor(app: Application) {
		super()
		this.app = app
		this.init()
		this.layout()
		this.subscribeToStore()
		this.app.ticker.add(this.update, this)
	}

	private get crashState() {
		return selectCrashGame(store.getState())
	}

	destroy(options?: boolean | { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
		this.app.ticker.remove(this.update, this)
		this.unsubscribeStore?.()
		this.unsubscribeStore = null
		if (this.countdownInterval) {
			clearInterval(this.countdownInterval)
		}
		super.destroy(options)
	}

	private init() {
		this.cloudTexture = Texture.from('cloud')
		this.patternTexture = Texture.from('bg_pattern')

		this.blast = Spine.from({
			skeleton: 'blastSpine',
			atlas: 'blastSpineAtlas',
		})

		this.bgLayer = new Container()
		this.bg = new Sprite(Texture.from('bg'))
		this.bg.anchor.set(0, 1)
		this.bgLayer.addChild(this.bg)

		this.cloudsContainer = new Container()

		this.timerText = new Text({
			text: '0',
			style: {
				fill: '#ffffff',
				fontSize: 40,
				fontWeight: 'bold',
			},
		})

		this.placeBetText = new Text({
			text: 'Place Your Bets!',
			style: {
				fill: '#060303',
				fontSize: 40,
				fontWeight: 'bold',
			},
		})

		this.timerText.visible = false
		this.placeBetText.visible = false

		this.plane = Spine.from({
			skeleton: 'jetSpine',
			atlas: 'jetSpineAtlas',
		})

		const bounds = this.plane.getLocalBounds()
		this.plane.pivot.set(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2)
		this.plane.state.setAnimation(0, 'Rest', true)

		this.multiplierText = new Text({
			text: '1.00x',
			style: {
				fontFamily: 'Montserrat-Bold',
				fill: '#ffd54a',
				fontSize: 96,
				fontWeight: '400',
			},
		})

		this.skyOverlay = new Graphics()
		this.areaMask = new Graphics()
		this.addChild(this.areaMask)
		this.mask = this.areaMask
		// sky → clouds → runway → plane/ui (clouds stay visible once the runway drifts away)
		this.addChild(this.skyOverlay, this.cloudsContainer, this.bgLayer, this.plane, this.multiplierText, this.timerText, this.placeBetText)
	}

	resize(width: number, height: number) {
		this.viewWidth = width
		this.viewHeight = height
		this.skyOverlay.clear().rect(0, 0, width, height).fill(0x3bc5fe)
		this.areaMask.clear().rect(0, 0, width, height).fill(0xffffff)
		this.layout()

		if (!this.bgDrift) {
			this.resetBgLayer()
		}

		this.ensureClouds()
	}

	private ensureClouds() {
		const area = Math.max(this.viewWidth * this.viewHeight, 1)
		const cloudCount = Math.floor(area / 90000) + 12

		while (this.clouds.length < cloudCount) {
			const cloud = new Sprite(this.cloudTexture) as CloudSprite
			cloud.anchor.set(0.5)
			const scale = 0.18 + Math.random() * 0.35
			cloud.scale.set(scale)
			cloud.alpha = 0
			cloud.visible = false
			cloud.baseSpeed = 0.8 + Math.random() * 0.6
			cloud.flow = 0.7 + Math.random() * 0.6
			this.clouds.push(cloud)
			this.cloudsContainer.addChild(cloud)
		}
	}

	private updateClouds() {
		const multiplier = this.crashState.currentMultiplier

		for (const cloud of this.clouds) {
			let speed = cloud.baseSpeed + multiplier * 3
			speed = Math.min(speed, 8)
			const depth = cloud.scale.x
			cloud.x -= speed * depth * cloud.flow
			cloud.y += speed * depth * cloud.flow

			const margin = 400
			if (cloud.x < -margin || cloud.y > this.flyHeight + margin) {
				this.spawnCloud(cloud)
			}
		}
	}

	private spawnCloud(cloud: Sprite) {
		const margin = 350
		if (Math.random() < 0.5) {
			cloud.x = this.flyWidth + Math.random() * 200
			cloud.y = -margin + Math.random() * this.flyHeight * 0.7
		} else {
			cloud.x = -margin + Math.random() * this.flyWidth * 1.2
			cloud.y = -margin - Math.random() * 200
		}
	}

	private ensureBgPatterns() {
		if (!this.patternTileWidth || this.flyWidth <= 0) {
			return
		}

		const needed = Math.ceil((this.flyWidth * 2) / this.patternTileWidth) + 4

		while (this.bgPatterns.length < needed) {
			const tile = new Sprite(this.patternTexture)
			tile.anchor.set(0, 1)
			tile.scale.set(this.bgScale)
			this.bgPatterns.push(tile)
			this.bgLayer.addChild(tile)
		}

		let x = this.bg.width
		for (const tile of this.bgPatterns) {
			tile.scale.set(this.bgScale)
			tile.x = x
			tile.y = 0
			x += this.patternTileWidth
		}
	}

	private resetBgLayer() {
		this.bgLayer.position.set(0, this.flyHeight)
		this.bg.x = 0
		this.bg.y = 0
		this.ensureBgPatterns()
	}

	private updateBgPatterns() {
		if (!this.patternTileWidth || this.bgPatterns.length === 0) {
			return
		}

		const recycleLeft = -this.patternTileWidth * 2

		for (const tile of this.bgPatterns) {
			const worldX = this.bgLayer.x + tile.x
			if (worldX + this.patternTileWidth >= recycleLeft) {
				continue
			}

			let maxX = this.bg.width
			for (const other of this.bgPatterns) {
				if (other !== tile && other.x > maxX) {
					maxX = other.x
				}
			}

			tile.x = maxX + this.patternTileWidth
		}
	}

	private update = () => {
		const { elapsedSeconds, currentMultiplier: multiplier } = this.crashState

		if (this.bgDrift) {
			const speed = 2 + Math.log(multiplier + 1) * 0.8
			this.bgLayer.x -= speed * 10

			if (this.serverTime > RUNWAY_TIME) {
				const liftTime = this.serverTime - RUNWAY_TIME
				const t = Math.min(liftTime / 1.1, 1)
				const ease = t * t * t
				this.bgLayer.y += speed * 12 * ease
			}

			this.updateBgPatterns()
		}

		if (this.cloudsActive) {
			this.updateClouds()
		}

		if (!this.isFlying) {
			return
		}

		const now = performance.now()
		const dt = (now - this.lastUpdate) / 1000

		if (elapsedSeconds > 0) {
			this.serverTime = elapsedSeconds
		} else {
			this.serverTime += dt
		}

		if (!this.takeoffPlayed && this.serverTime >= RUNWAY_TIME) {
			this.takeoffPlayed = true
			const takeoff = this.plane.state.setAnimation(0, 'Take-off', false)
			takeoff.timeScale = 1
			const fly = this.plane.state.addAnimation(0, 'Flying', true, 0)
			fly.timeScale = 0.35
		}

		this.flyPlane(this.serverTime)
		if (this.isCrashed) {
			this.blast.update(dt)
		}
		this.plane.update(dt)
		this.lastUpdate = now
	}

	private layout() {
		this.flyWidth = this.viewWidth
		this.flyHeight = this.viewHeight

		const shortest = Math.min(this.flyWidth, this.flyHeight)
		const scale = Math.min(Math.max(shortest / 720, 0.55), 1.15)

		this.startX = this.flyWidth * 0.16
		this.startY = this.flyHeight * 0.66

		const centerX = this.flyWidth / 2
		const centerY = this.flyHeight / 2

		this.multiplierText.anchor.set(0.5)
		this.multiplierText.position.set(centerX, centerY)
		this.timerText.anchor.set(0.5)
		this.timerText.position.set(centerX, centerY - 88 * scale)
		this.placeBetText.anchor.set(0.5)
		this.placeBetText.position.set(centerX, centerY + 60 * scale)
		this.multiplierText.style.fontSize = 96 * scale
		this.timerText.style.fontSize = 60 * scale
		this.placeBetText.style.fontSize = 36 * scale

		if (!this.isFlying && !this.isCrashed) {
			this.plane.position.set(this.startX, this.startY)
		}

		this.plane.scale.set((shortest / 900) * PLANE_SCALE_FACTOR)

		if (this.bg.texture.height > 0 && this.flyHeight > 0) {
			const scaleX = this.flyWidth / this.bg.texture.width
			const scaleY = this.flyHeight / this.bg.texture.height
			this.bgScale = Math.max(scaleX, scaleY)
			this.bg.scale.set(this.bgScale)
			this.patternTileWidth = this.patternTexture.width * this.bgScale
			this.ensureBgPatterns()

			if (!this.bgDrift) {
				this.resetBgLayer()
			}
		}
	}

	private onRoundWaiting = (seconds: number) => {
		if (this.isWaiting) {
			return
		}

		this.removeChild(this.blast)
		this.plane.visible = true
		this.bgDrift = false
		this.resetBgLayer()
		this.isCrashed = false
		this.isWaiting = true
		this.isFlying = false
		this.flyLoopStarted = false
		this.serverTime = 0
		this.lastUpdate = 0
		this.resetScene()
		this.resetPlane()
		this.waitTimer(seconds)
	}

	private onRoundStart = () => {
		this.takeoffPlayed = false
		this.flyLoopStarted = false
		this.bgDrift = true
		this.skyAnimating = true
		this.isWaiting = false
		this.serverTime = 0
		this.lastUpdate = performance.now()
		this.plane.position.set(this.startX, this.startY)
		this.plane.rotation = 0
		this.isFlying = true

		SoundManager.instance.stop('takeOff')
		SoundManager.instance.play('takeOff', { volume: SFX_VOLUME_TAKEOFF, loop: false })
	}

	private onPlaneCrash = (crashPoint: number) => {
		this.isFlying = false
		this.crashPlane(crashPoint)
	}

	private subscribeToStore() {
		this.previousPhase = this.crashState.phase

		this.unsubscribeStore = store.subscribe(() => {
			const crash = this.crashState

			if (crash.phase === this.previousPhase) {
				return
			}

			const nextPhase = crash.phase
			this.previousPhase = nextPhase

			if (nextPhase === 'waiting') {
				this.onRoundWaiting(crash.bettingWindowSeconds)
				return
			}

			if (nextPhase === 'flying') {
				this.onRoundStart()
				return
			}

			if (nextPhase === 'crashed') {
				this.onPlaneCrash(crash.crashPoint)
			}
		})
	}

	private resetScene() {
		this.cloudsActive = false
		SoundManager.instance.stop('crash')
		SoundManager.instance.stop('fly')
		SoundManager.instance.stop('takeOff')

		this.clouds.forEach((cloud) => {
			gsap.to(cloud, {
				alpha: 0,
				duration: 0.3,
				onComplete: () => {
					cloud.visible = false
				},
			})
		})
	}

	private flyPlane(time: number) {
		const multiplier = this.crashState.currentMultiplier
		this.multiplierText.visible = true
		this.multiplierText.style.fontFamily = 'Montserrat-Bold'
		this.multiplierText.style.fill = '#ffd54a'
		this.multiplierText.text = `${multiplier.toFixed(2)}x`

		const maxX = this.flyWidth - this.flyWidth * 0.25
		const horizontalTravel = this.flyWidth * 0.8
		const speed = horizontalTravel / 2.2
		const x = Math.min(this.startX + time * speed, maxX)
		const reachedCruise = x >= maxX

		if (!this.cloudsActive && time > RUNWAY_TIME + 0.15) {
			this.cloudsActive = true
			this.ensureClouds()
			this.clouds.forEach((cloud) => {
				const margin = 400
				if (Math.random() < 0.5) {
					cloud.x = this.flyWidth + Math.random() * 200
					cloud.y = -margin + Math.random() * this.flyHeight * 0.6
				} else {
					cloud.x = -margin + Math.random() * this.flyWidth * 1.2
					cloud.y = -margin
				}
				cloud.visible = true
				gsap.fromTo(cloud, { alpha: 0 }, { alpha: 0.85, duration: 0.8 })
			})
		}

		const cruiseY = this.flyHeight * 0.25
		let y: number

		if (time < RUNWAY_TIME) {
			y = this.startY
		} else {
			const flyTime = time - RUNWAY_TIME
			const climbY = this.startY - Math.pow(flyTime, 2) * (this.flyHeight * PLANE_CLIMB_FACTOR)

			if (climbY > cruiseY) {
				y = climbY
			} else {
				const t = performance.now() * 0.004
				const hoverOffset = Math.sin(t) * (this.flyHeight * 0.006) + Math.sin(t * 2.3) * (this.flyHeight * 0.003) + Math.sin(t * 5.7) * (this.flyHeight * 0.0015)
				y = cruiseY + hoverOffset
			}
		}

		this.plane.position.set(x, y)

		if (time > RUNWAY_TIME) {
			if (time > RUNWAY_TIME + TAKEOFF_SFX_EXTRA_TIME) {
				SoundManager.instance.stop('takeOff')
				if (!this.flyLoopStarted) {
					this.flyLoopStarted = true
					SoundManager.instance.play('fly', { volume: SFX_VOLUME_FLY, loop: true })
				}
			}
			const flyTime = time - RUNWAY_TIME
			const targetRotation = Math.min(flyTime * 0.4, 0.45)
			this.plane.rotation = reachedCruise ? -0.45 : -targetRotation
		} else {
			this.plane.rotation = 0
		}
	}

	private waitTimer(seconds: number) {
		if (!seconds || seconds <= 0) {
			return
		}

		this.placeBetText.alpha = 1
		this.placeBetText.scale.set(1)
		this.placeBetText.visible = true
		this.timerText.visible = true
		this.timerText.style.fill = 0xffffff
		this.multiplierText.visible = false
		this.layout()

		if (this.countdownInterval) {
			clearInterval(this.countdownInterval)
		}

		let remaining = Math.floor(seconds)
		this.timerText.text = remaining.toString()

		const pulse = () => {
			SoundManager.instance.play('timer', { volume: SFX_VOLUME_TIMER })
			gsap.killTweensOf(this.timerText.scale)
			gsap.killTweensOf(this.timerText)
			this.timerText.scale.set(1)
			gsap.fromTo(
				this.timerText.scale,
				{ x: TIMER_PULSE_SCALE_FROM, y: TIMER_PULSE_SCALE_FROM },
				{ x: TIMER_PULSE_SCALE_TO, y: TIMER_PULSE_SCALE_TO, duration: 0.45, ease: 'power2.out', yoyo: true, repeat: 1 },
			)
			gsap.fromTo(this.timerText, { alpha: 0.5 }, { alpha: 1, duration: 0.45, yoyo: true, repeat: 1 })
		}

		pulse()

		this.countdownInterval = setInterval(() => {
			remaining -= 1
			if (remaining <= 0) {
				clearInterval(this.countdownInterval)
				gsap.to([this.timerText, this.placeBetText], {
					scale: COUNTDOWN_EXIT_SCALE,
					alpha: 0,
					duration: COUNTDOWN_EXIT_DURATION,
					ease: 'power3.out',
					onComplete: () => {
						this.timerText.visible = false
						this.placeBetText.visible = false
						this.timerText.scale.set(1)
						this.timerText.alpha = 1
						this.placeBetText.scale.set(1)
						this.placeBetText.alpha = 1
					},
				})
			} else {
				this.timerText.text = remaining.toString()
				pulse()
			}
		}, COUNTDOWN_INTERVAL_MS)
	}

	private crashPlane(rate: number) {
		SoundManager.instance.stop('fly')
		SoundManager.instance.play('crash', { volume: SFX_VOLUME_CRASH })
		this.isCrashed = true
		this.cloudsActive = false
		this.plane.visible = false
		this.blast.x = this.plane.x
		this.blast.y = this.plane.y

		const bounds = this.blast.getLocalBounds()
		this.blast.pivot.set(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2)
		const baseScale = Math.min(this.flyWidth, this.flyHeight) / 800
		this.blast.scale.set(baseScale * BLAST_SCALE_FACTOR)
		this.blast.state.setAnimation(0, 'Blast', false)
		this.addChild(this.blast)
		this.bgDrift = false
		this.skyAnimating = false
		this.multiplierText.text = `${rate.toFixed(2)}x`
		this.multiplierText.style.fill = 0xff0000
		this.multiplierText.style.fontFamily = 'Montserrat-Bold'
	}

	private resetPlane() {
		this.takeoffPlayed = false
		this.flyLoopStarted = false
		this.plane.state.setAnimation(0, 'Rest', true)
		this.plane.rotation = 0
		gsap.killTweensOf(this.plane)
		this.plane.position.set(-120, this.startY)
		gsap.to(this.plane, { x: this.startX, duration: PLANE_ENTRY_DURATION, ease: 'power2.out' })
		this.skyAnimating = false
		this.skyOverlay.clear().rect(0, 0, this.viewWidth, this.viewHeight).fill(0x3bc5fe)
	}
}
